import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * 🎯 HYBRID TTS SERVICE
 * 
 * Serviço que implementa TTS híbrido:
 * ✅ PRIORIZA vozes IA (ElevenLabs via backend)
 * ✅ FALLBACK transparente para TTS nativo do browser
 * ✅ ZERO downtime - sempre funciona
 * ✅ Fluxo simplificado: Som gerenciado pelo alert-display → TTS direto
 */

export interface TTSRequest {
  text: string;
  streamerId: string;
  alertType?: 'DONATION' | 'SALE' | 'FOLLOW';
  donationId?: string;
  fallbackVoice?: string;
}

export interface TTSResponse {
  success: boolean;
  audioUrl?: string;
  duration?: number;
  voiceUsed: 'ai' | 'fallback' | 'failed' | 'cached';
  voiceName?: string;
  error?: string;
  forcedRate?: number; // 🎯 Velocidade forçada para donations (1.0x)
  metrics: {
    aiAttempted: boolean;
    aiSucceeded: boolean;
    fallbackUsed: boolean;
    responseTime: number;
    charactersUsed: number;
  };
}

export interface DonationTTSOptions {
  customText?: string;
  includeAmount?: boolean;
  includeDonor?: boolean;
  fallbackVoice?: string;
}

@Injectable({
  providedIn: 'root'
})
export class HybridTTSService {
  private readonly apiUrl = environment.backend;
  private speechSynthesis: SpeechSynthesis | null = null;
  private currentAudio?: HTMLAudioElement;
  private shouldStopAudio = false;
  private musicthonConfig: any = null; // 🎵 Config do Musicthon para processamento de áudio

  constructor(private http: HttpClient) {
    this.initializeFallbackTTS();
  }

  /**
   * 🎵 Atualizar configuração do Musicthon (chamado pelo componente de alertas)
   */
  setMusicthonConfig(config: any): void {
    this.musicthonConfig = config;
    console.log('🎵 [HybridTTS] Musicthon config atualizada', { useMobileGain: config?.useMobileGain });
  }

  /**
   * 📱 Processar URL de áudio para aplicar gain se useMobileGain estiver ativo
   */
  processAudioUrl(
    originalUrl: string,
    donationId: string,
    audioType: 'tts' | 'voice_recording' | 'steal' | 'coin',
    streamerId: string
  ): string {
    const config = this.musicthonConfig;
    
    console.log('🚀🚀🚀 [HybridTTS] processAudioUrl CHAMADO!', { 
      originalUrl, 
      donationId, 
      audioType, 
      streamerId, 
      useMobileGain: config?.useMobileGain 
    });
    
    // Se useMobileGain não estiver ativo, retorna URL original
    if (!config?.useMobileGain) {
      console.log('⚠️ [HybridTTS] useMobileGain DESATIVADO - retornando URL original');
      return originalUrl;
    }

    // Construir URL do endpoint de processamento (RELATIVA para passar pelo proxy)
    const processedUrl = `/api/v1/audio/processed/${donationId}?audioType=${audioType}&streamerId=${streamerId}`;
    console.log('✅ [HybridTTS] useMobileGain ATIVO - retornando URL processada:', processedUrl);
    return processedUrl;
  }

  /**
   * 🎤 Gerar TTS híbrido para donation
   */
  async generateDonationTTS(donationId: string, options: DonationTTSOptions = {}): Promise<TTSResponse> {
    try {
      const url = `${this.apiUrl}/donations/${donationId}/generate-tts`;
      const response = await firstValueFrom(
        this.http.post<any>(url, options)
      );
      return response;
    } catch (error) {
      console.error('Failed to generate donation TTS:', error);
      return {
        success: false,
        voiceUsed: 'failed',
        error: `TTS_REQUEST_FAILED: ${error}`,
        metrics: {
          aiAttempted: false,
          aiSucceeded: false,
          fallbackUsed: false,
          responseTime: 0,
          charactersUsed: 0,
        }
      };
    }
  }

  /**
   * 🎵 Reproduzir áudio baseado na resposta do TTS híbrido
   */
  async playTTSResponse(response: TTSResponse, settings: any, donationId?: string, streamerId?: string): Promise<void> {
    if (!response.success) {
      console.error('Cannot play failed TTS response:', response.error);
      return;
    }

    // 🎤 Se tem audioUrl (AI ou cached), reproduzir
    if ((response.voiceUsed === 'ai' || response.voiceUsed === 'cached') && response.audioUrl) {
      // 🎚️ Processar URL para Moblin/iOS se necessário
      let processedUrl = response.audioUrl;
      if (donationId && streamerId) {
        processedUrl = this.processAudioUrl(response.audioUrl, donationId, 'tts', streamerId);
      }
      
      // 🤖 Reproduzir áudio da IA (gerado ou cached) - AGUARDAR término
      await firstValueFrom(this.playAIAudio(processedUrl, settings));
    } else if (response.voiceUsed === 'fallback') {
      // 🔄 Usar TTS nativo do browser
      await this.playFallbackTTS(response, settings);
    }
  }

  /**
   * 🤖 Reproduzir áudio da IA (RxJS Observable)
   */
  playAIAudio(audioUrl: string, settings?: any, isVoiceRecording?: boolean): Observable<void> {
    const PLAYBACK_TIMEOUT_MS = 40000; // 40 segundos para acomodar gravações mais longas

    return new Observable(observer => {
      // 🛑 Se já foi solicitado stop antes de criar o Observable, cancelar imediatamente
      if (this.shouldStopAudio) {
        console.log('🚫 [HybridTTS] playAIAudio CANCELADO - shouldStopAudio=true antes de iniciar');
        observer.complete();
        return;
      }
      
      // 🔧 Ajuste para URLs relativas
      let fullUrl: string;
      if (audioUrl.startsWith('/api/v1/audio/processed/')) {
        // 🎚️ URL do endpoint de processamento
        // Em dev (ng serve com proxy): usar window.location.origin para passar pelo proxy
        // Em prod (sem proxy): usar environment.backend
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          // Desenvolvimento: ng serve com proxy - usar origem local
          fullUrl = `${window.location.origin}${audioUrl}`;
        } else {
          // Produção: sem proxy - usar backend direto
          fullUrl = `${this.apiUrl.replace('/api/v1', '')}${audioUrl}`;
        }
      } else if (audioUrl.startsWith('/api/v1/')) {
        // URL já tem /api/v1, usar apenas a base (sem /api/v1)
        const baseUrl = this.apiUrl.replace('/api/v1', '');
        fullUrl = `${baseUrl}${audioUrl}`;
      } else if (audioUrl.startsWith('/')) {
        // URL relativa normal
        fullUrl = `${this.apiUrl}${audioUrl}`;
      } else {
        // URL absoluta
        fullUrl = audioUrl;
      }
      
      // 🎯 Criar áudio e inserir no DOM com ID fixo para poder destruir depois
      const audioId = 'tts-audio-player';
      
      // Remover qualquer áudio anterior que ainda exista
      const existingAudio = document.getElementById(audioId);
      if (existingAudio) {
        console.log('🗑️ [HybridTTS] Removendo áudio anterior do DOM');
        existingAudio.remove();
      }
      
      const audio = new Audio(fullUrl);
      audio.id = audioId;
      audio.setAttribute('playsinline', '');
      audio.autoplay = true;
      audio.style.display = 'none'; // Esconder elemento
      document.body.appendChild(audio); // Adicionar ao DOM
      
      this.currentAudio = audio;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let checkInterval: ReturnType<typeof setInterval> | null = null;

      console.log('🎧 [HybridTTS] playAIAudio init (DOM)', { fullUrl, audioId, isVoiceRecording, volume: audio.volume });

      // 🛑 Verificar novamente após criar o áudio (pode ter sido cancelado durante a criação)
      if (this.shouldStopAudio) {
        console.log('🚫 [HybridTTS] playAIAudio CANCELADO - shouldStopAudio=true após criar audio');
        this.currentAudio = undefined;
        observer.complete();
        return;
      }

      const clearTimers = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
        }
      };

      const cleanup = (error?: Error) => {
        clearTimers();
        
        // Remover áudio do DOM pelo ID
        const audioElement = document.getElementById(audioId);
        if (audioElement) {
          console.log('🗑️ [HybridTTS] Removendo e parando áudio do DOM');
          (audioElement as HTMLAudioElement).pause();
          audioElement.remove();
        }
        
        // Limpar referência
        if (this.currentAudio) {
          this.currentAudio.pause();
          this.currentAudio.currentTime = 0;
          this.currentAudio = undefined;
        }
        
        // Garantir que o áudio local também seja parado
        if (audio && !audio.paused) {
          audio.pause();
          audio.currentTime = 0;
        }
        
        if (error) {
          observer.error(error);
        }
      };

      timeoutId = setTimeout(() => {
        console.warn('⏰ [HybridTTS] TIMEOUT ao reproduzir áudio AI:', fullUrl);
        cleanup(new Error('AI audio timeout'));
      }, PLAYBACK_TIMEOUT_MS);
      
      // 🎯 Aplicar volume correto (settings já vem em 0-1, não precisa dividir)
      if (settings) {
        if (isVoiceRecording && settings.audioRecordingVolume !== undefined) {
          audio.volume = settings.audioRecordingVolume; // Já vem 0-1
        } else if (settings.speechVolume !== undefined) {
          audio.volume = settings.speechVolume; // Já vem 0-1
        }
      }
      
      // ✅ Emitir quando áudio TERMINAR de tocar
      audio.onended = () => {
        clearTimers();
        this.currentAudio = undefined;
        console.log('✅ [HybridTTS] playAIAudio finished', { fullUrl });
        observer.next();
        observer.complete();
      };
      
      audio.onerror = (error) => {
        console.error('❌ Erro ao carregar áudio:', error);
        console.error('📍 URL tentada:', fullUrl);
        cleanup(new Error('Failed to load AI audio'));
      };
      
      // 🛑 Verificar mais uma vez antes de play()
      if (this.shouldStopAudio) {
        console.log('🚫 [HybridTTS] playAIAudio CANCELADO - shouldStopAudio=true antes de play()');
        cleanup();
        observer.complete();
        return;
      }
      
      // 🎵 Iniciar reprodução
      audio.play().then(() => {
        console.log('▶️ [HybridTTS] Audio playing started');
        
        // 🛑 Verificar periodicamente se devemos parar durante a reprodução
        checkInterval = setInterval(() => {
          if (this.shouldStopAudio) {
            console.log('🛑 [HybridTTS] shouldStopAudio detectado durante reprodução, parando...');
            cleanup();
            observer.complete();
          }
        }, 100); // Verificar a cada 100ms
      }).catch((playError) => {
        console.warn('⚠️ Autoplay blocked:', playError);
        clearTimers();
        this.currentAudio = undefined;
        observer.next();
        observer.complete();
      });

      // 🧹 Cleanup ao cancelar subscription
      return () => {
        cleanup();
      };
    });
  }

  stopCurrentAudio(): void {
    console.log('🛠️ [HybridTTS] stopCurrentAudio chamado', {
      hasCurrentAudio: !!this.currentAudio,
      audioSrc: this.currentAudio?.src,
      audioCurrentTime: this.currentAudio?.currentTime,
      audioPaused: this.currentAudio?.paused,
    });
    
    // 🛑 Marcar flag para impedir novos áudios
    this.shouldStopAudio = true;
    console.log('🛑 [HybridTTS] shouldStopAudio = true');
    
    // 🎯 BUSCAR E DESTRUIR áudio do DOM pelo ID
    const audioElement = document.getElementById('tts-audio-player') as HTMLAudioElement;
    if (audioElement) {
      try {
        console.log('🗑️ [HybridTTS] Áudio encontrado no DOM, pausando e removendo...', {
          src: audioElement.src,
          paused: audioElement.paused,
          currentTime: audioElement.currentTime
        });
        audioElement.pause();
        audioElement.currentTime = 0;
        audioElement.remove();
        console.log('✅ [HybridTTS] Áudio removido do DOM');
      } catch (error) {
        console.error('❌ [HybridTTS] Erro ao parar áudio do DOM:', error);
      }
    } else {
      console.log('⚠️ [HybridTTS] Nenhum áudio encontrado no DOM com ID tts-audio-player');
    }
    
    // Limpar referência também
    if (this.currentAudio) {
      try {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
      } catch (error) {
        console.error('❌ [HybridTTS] Erro ao parar currentAudio:', error);
      }
      this.currentAudio = undefined;
    }
  }

  /**
   * 🔄 Resetar flag de stop para permitir novos áudios
   */
  resetStopFlag(): void {
    console.log('🔄 [HybridTTS] resetStopFlag - shouldStopAudio = false');
    this.shouldStopAudio = false;
  }

  /**
   * 🔄 Reproduzir TTS nativo (fallback)
   */
  private async playFallbackTTS(response: TTSResponse, settings: any): Promise<void> {
    // Extrair texto do response (assumindo que está em speechText)
    const text = (response as any).speechText || 'Nova doação recebida!';
    
    // 🎯 Usar forcedRate se disponível
    const rate = response.forcedRate;
    await this.playBrowserTTS(text, settings, rate);
  }

  /**
   * 🧹 Remover códigos SSML/ElevenLabs que o TTS nativo não entende
   */
  private cleanSSMLTags(text: string): string {
    if (!text) return text;
    
    // Remove tags <break time="..."/> usadas pelo ElevenLabs
    return text.replace(/<break\s+time="[^"]*"\s*\/>/gi, '');
  }

  /**
   * 🎤 Reproduzir TTS do browser
   */
  private async playBrowserTTS(text: string, settings: any, forcedRate?: number): Promise<void> {
    if (!this.speechSynthesis) {
      console.warn('⚠️ Speech synthesis not available');
      return;
    }

    return new Promise((resolve) => {
      const voices = this.speechSynthesis!.getVoices();
      
      const selectedVoice = voices.find(voice => voice.voiceURI === settings.speechVoice) ||
                           voices.find(voice => voice.lang.startsWith('pt')) ||
                           voices[0] || null;

      // 🧹 Limpar códigos SSML antes de passar para o TTS nativo
      const cleanedText = this.cleanSSMLTags(text);

      const utterance = new SpeechSynthesisUtterance(cleanedText);
      utterance.lang = selectedVoice?.lang || 'pt-BR';
      utterance.voice = selectedVoice;
      
      // 🎯 Para donations e vendas, usar velocidade fixa 1.0x
      utterance.rate = forcedRate !== undefined ? forcedRate : (settings.speechRate || 1);
      utterance.pitch = settings.speechPitch || 1;
      utterance.volume = 1.0; // 🎯 VOLUME FIXO - coin.wav já aplicou o volume configurado

      utterance.onend = () => resolve();
      utterance.onerror = () => {
        console.warn('⚠️ TTS error, resolving anyway');
        resolve();
      };

      this.speechSynthesis!.cancel(); // Limpar fila
      this.speechSynthesis!.speak(utterance);
    });
  }

  /**
   * 🔄 Fluxo de fallback (método tradicional)
   */
  private async playFallbackFlow(donationData: any, settings: any): Promise<void> {
    const template = '{username} doou {amount}. {message}';
    const message = this.replacePlaceholders(template, donationData);
    
    // 🎯 Para donations, sempre usar velocidade 1.0x no fallback
    await this.playBrowserTTS(message, settings, 1.0);
  }



  /**
   * 💰 Formatar valor
   */
  private formatAmount(amount: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount || 0);
  }

  /**
   * 🔧 Substituir placeholders (compatibilidade)
   */
  private replacePlaceholders(template: string, data: any): string {
    const amount = this.formatAmount(data.amount);
    return template
      .replace('${username}', data.username || 'Anônimo')
      .replace('${amount}', amount)
      .replace('${message}', data.message || '');
  }

  /**
   * 🛠️ Inicializar TTS nativo para fallback
   */
  private initializeFallbackTTS(): void {
    if ('speechSynthesis' in window) {
      this.speechSynthesis = window.speechSynthesis;
    }
  }

  // ==================== SALES TTS METHODS ====================

  /**
   * 🛒 Gerar TTS híbrido para sales (vendas)
   * 
   * Diferente das doações:
   * - Usa voz padrão única do streamer
   * - Template customizável
   * - Sem pausa entre partes
   */
  async generateSalesTTS(orderId: string): Promise<TTSResponse> {
    try {
      const url = `${this.apiUrl}/orders/${orderId}/generate-sales-tts`;
      
      const response = await firstValueFrom(
        this.http.post<any>(url, {})
      );

      return response;

    } catch (error) {
      console.error('❌ Sales TTS Error:', error);
      
      return {
        success: false,
        voiceUsed: 'failed',
        error: `SALES_TTS_FAILED: ${error}`,
        metrics: {
          aiAttempted: false,
          aiSucceeded: false,
          fallbackUsed: false,
          responseTime: 0,
          charactersUsed: 0,
        }
      };
    }
  }

  /**
   * 🎵 Fluxo completo para sales: Som → TTS (usando voz padrão do streamer)
   */
  async playSalesFlow(salesData: any, settings: any): Promise<void> {
    try {
      // 1. 🔊 Som de moeda já é tocado pelo alert-display.component.ts

      // 2. 🎤 TTS usando voz padrão do streamer (se disponível o orderId)
      if (salesData.id) {
        try {
          const ttsResponse = await this.generateSalesTTS(salesData.id);
          
          if (ttsResponse.success) {
            // ⚠️ Para sales, não temos donationId, então passamos saleId e streamerId se disponível
            const streamerId = salesData.streamerId;
            await this.playTTSResponse(ttsResponse, settings, salesData.id, streamerId);
          } else {
            await this.playSalesFallbackTTS(salesData, settings);
          }
        } catch (error) {
          console.error('❌ Erro no TTS de vendas:', error);
          await this.playSalesFallbackTTS(salesData, settings);
        }
      } else {
        await this.playSalesFallbackTTS(salesData, settings);
      }

    } catch (error) {
      console.error('❌ Erro no fluxo de vendas:', error);
    }
  }

  /**
   * 🔄 Fallback TTS para vendas usando browser nativo
   * Template FIXO: {username} comprou {quantity} {product}
   */
  private async playSalesFallbackTTS(salesData: any, settings: any): Promise<void> {
    if (!this.speechSynthesis || !settings.speechEnabled) {
      return;
    }

    // 🔒 Template FIXO para vendas
    const FIXED_TEMPLATE = '{username} comprou {quantity} {product}';
    const message = this.replaceSalesPlaceholders(FIXED_TEMPLATE, salesData);
    
    // 🎯 VELOCIDADE FIXA (1.0x) para vendas - igual às doações
    await this.playBrowserTTS(message, settings, 1.0);
  }

  /**
   * �📝 Substituir placeholders para vendas
   * Template FIXO: {username} comprou {quantity} {product}
   */
  private replaceSalesPlaceholders(template: string, data: any): string {
    return template
      .replace('{username}', data.username || 'Alguém')
      .replace('{quantity}', (data.quantity || 1).toString())
      .replace('{product}', data.productStreamerName || 'produto');
  }
}
